import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import PrintModal from '../../../../components/PrintModal';
import Modal from '../../../../components/Modal';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency } from '../../../../utils/formatters';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { usePermesso } from '../../../../context/PermessiContext';
import { API_DOCS } from './ProformaAccontoDettaglio.api';
import './ProformaAccontoDettaglio.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoProformaAcconto = 'DA_INCASSARE' | 'PAGATA' | 'ANNULLATA';
type OrigineProformaAcconto = 'DA_CONVERSIONE_ORDINE' | 'MANUALE_COMMESSA';
type SceltaIncasso = 'CHIUDI_A_IMPORTO_REALE' | 'LASCIA_APERTA';

interface Contabile {
  id: number;
  nome: string;
  nomeFile: string;
}

interface FatturaRef {
  id: number;
  numero: string | null;
  statoFattura: 'PROFORMA' | 'SDI';
}

interface Movimento {
  id: number;
  importo: string;
  dataIncasso: string;
  canale: string | null;
  note: string | null;
  registratoAt: string;
  contabile: Contabile | null;
  fattura: FatturaRef | null;
}

interface LogRow {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface ProformaDettaglio {
  id: number;
  numero: string;
  origine: OrigineProformaAcconto;
  stato: StatoProformaAcconto;
  imponibile: string;
  iva: string;
  totale: string;
  clienteRagioneSociale: string | null;
  dataEmissione: string;
  dataPagamento: string | null;
  dataAnnullamento: string | null;
  motivoAnnullamento: string | null;
  societaId: number;
  commessa: { id: number; numero: string } | null;
  ordine: { id: number; numero: string } | null;
  acquirente: { id: number; nome: string; cognome: string } | null;
  movimenti: Movimento[];
  logs: LogRow[];
  totaleIncassato: number;
  residuo: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoProformaAcconto, string> = {
  DA_INCASSARE: 'Da incassare',
  PAGATA: 'Pagata',
  ANNULLATA: 'Annullata',
};

const STATO_BADGE: Record<StatoProformaAcconto, string> = {
  DA_INCASSARE: 'bop-badge-neutral',
  PAGATA: 'bop-badge-success',
  ANNULLATA: 'bop-badge-neutral',
};

const ORIGINE_LABEL: Record<OrigineProformaAcconto, string> = {
  DA_CONVERSIONE_ORDINE: 'Da ordine',
  MANUALE_COMMESSA: 'Manuale',
};

const ORIGINE_BADGE: Record<OrigineProformaAcconto, string> = {
  DA_CONVERSIONE_ORDINE: 'bop-badge-info',
  MANUALE_COMMESSA: 'bop-badge-neutral',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT');
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function ProformaAccontoDettaglio() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermesso();
  const proformaId = parseInt(id ?? '0', 10);

  const [data, setData] = useState<ProformaDettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalIncasso, setModalIncasso] = useState(false);
  const [modalAnnulla, setModalAnnulla] = useState(false);
  // Stampa / invio al cliente: motore di stampa, modulo PROFORMA_ACCONTO
  const [modalStampa, setModalStampa] = useState(false);
  const [generandoFattura, setGenerandoFattura] = useState(false);

  const canEdit = can('commerciale.acconti', 'edit');

  useBloccoRecordContesto({
    recordSocietaId: data?.societaId,
    label: data ? `Proforma acconto ${data.numero}` : 'Proforma acconto',
  });

  const reload = useCallback(async () => {
    if (!proformaId) return;
    setLoading(true);
    try {
      // @api GET /api/proforme-acconto/:id -> {"success":true,"data":{"id":"number","numero":"string","origine":"string","stato":"string","imponibile":"string","iva":"string","totale":"string","clienteRagioneSociale":"string|null","dataEmissione":"string","dataPagamento":"string|null","dataAnnullamento":"string|null","motivoAnnullamento":"string|null","societaId":"number","commessa":{"id":"number","numero":"string"},"ordine":{"id":"number","numero":"string"}|null,"acquirente":{"id":"number","nome":"string","cognome":"string"}|null,"movimenti":[{"id":"number","importo":"string","dataIncasso":"string","canale":"string","note":"string|null","registratoAt":"string","contabile":{"id":"number","nome":"string","nomeFile":"string"}|null,"fattura":{"id":"number","numero":"string|null","statoFattura":"string"}|null}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string|null","createdAt":"string"}],"totaleIncassato":"number","residuo":"number"}}
      const res = await apiFetch<{ success: boolean; data: ProformaDettaglio; error?: string }>(
        `/api/proforme-acconto/${proformaId}`
      );
      if (!res.success) {
        setError(res.error || 'Errore caricamento proforma');
        setData(null);
      } else {
        setData(res.data);
        setError(null);
      }
    } catch (e) {
      const err = e as Error;
      setError(err.message || 'Errore');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [proformaId]);

  useEffect(() => { reload(); }, [reload]);

  // ── Azione: Genera fattura d'acconto (proforma PAGATA) ──
  const handleGeneraFattura = async () => {
    if (!data) return;
    if (!window.confirm('Emettere la fattura immediata per questo acconto?')) return;
    setGenerandoFattura(true);
    try {
      // @api POST /api/fatture/da-acconto {"proformaAccontoId":"number"} -> {"success":true,"data":{"id":"number"}}
      const r = await apiFetch<{ success: boolean; data?: { id: number } }>('/api/fatture/da-acconto', {
        method: 'POST',
        body: JSON.stringify({ proformaAccontoId: proformaId }),
      });
      if (!r.success || !r.data) {
        showToast(estraiErrore({ payload: r }, 'Errore nella generazione della fattura'), 'error');
        return;
      }
      showToast("Fattura d'acconto generata in bozza", 'success');
      navigate(`/commerciale/attivo/fatture/${r.data.id}`);
    } catch (err: unknown) {
      // 409 ACCONTO_NON_PAGATO | GIA_FATTURATO: message dettagliato dal backend
      showToast(estraiErrore(err, 'Errore nella generazione della fattura'), 'error');
    } finally {
      setGenerandoFattura(false);
    }
  };

  // ── Render guard ──
  if (loading) {
    return (
      <Layout>
        <div className="acconto-dett">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento proforma…</div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  if (error === 'Accesso negato') {
    return (
      <Layout>
        <div className="acconto-dett">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Non hai i permessi</h3>
              <p className="bop-empty-state__text">Non puoi visualizzare questa proforma.</p>
              <BopBackLink to="/commerciale/attivo/acconti" label="Torna agli acconti" />
            </div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="acconto-dett">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Proforma non trovata</h3>
              <p className="bop-empty-state__text">{error ?? 'La proforma richiesta non esiste o è stata eliminata.'}</p>
              <BopBackLink to="/commerciale/attivo/acconti" label="Torna agli acconti" />
            </div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  const acquirenteLabel = data.acquirente
    ? [data.acquirente.nome, data.acquirente.cognome].filter(Boolean).join(' ').trim()
    : null;

  const showRegistraIncasso = canEdit && data.stato === 'DA_INCASSARE';
  const showAnnulla = canEdit && data.stato === 'DA_INCASSARE' && data.movimenti.length === 0;
  const showGeneraFattura = data.stato === 'PAGATA' && can('commerciale.fatture_attive', 'edit');

  return (
    <Layout>
      <div className="acconto-dett">
        {/* Header */}
        <BopBackLink to="/commerciale/attivo/acconti" label="Torna agli acconti" />
        <header className="acconto-dett__header">
          <div className="acconto-dett__header-top">
            <div className="acconto-dett__breadcrumb">
              Commerciale ›{' '}
              <Link to="/commerciale/attivo/acconti">Acconti</Link> › {data.numero}
            </div>
          </div>

          <div className="acconto-dett__title-row">
            <h1 className="bop-page-title">Proforma acconto {data.numero}</h1>
            <div className="acconto-dett__actions">
              {showRegistraIncasso && (
                <button
                  type="button"
                  className="bop-btn-primary"
                  onClick={() => setModalIncasso(true)}
                >
                  Registra incasso
                </button>
              )}
              {showAnnulla && (
                <button
                  type="button"
                  className="bop-btn-danger"
                  onClick={() => setModalAnnulla(true)}
                >
                  Annulla proforma
                </button>
              )}
              {showGeneraFattura && (
                <button
                  type="button"
                  className="bop-btn-primary"
                  onClick={handleGeneraFattura}
                  disabled={generandoFattura}
                >
                  {generandoFattura ? 'Generazione…' : "Genera fattura d'acconto"}
                </button>
              )}
              <button
                type="button"
                className="bop-btn-secondary"
                onClick={() => setModalStampa(true)}
                title="Anteprima, PDF o invio email della proforma con il template di Impostazioni → Report"
              >
                Stampa / Invia
              </button>
            </div>
          </div>

          <div className="acconto-dett__badges">
            <span className={`bop-badge ${STATO_BADGE[data.stato]}`}>
              {STATO_LABEL[data.stato]}
            </span>
            <span className={`bop-badge ${ORIGINE_BADGE[data.origine]}`}>
              {ORIGINE_LABEL[data.origine]}
            </span>
          </div>
        </header>

        {/* Testata */}
        <section className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Testata</h2>
          </div>
          <div className="bop-card__body">
            <div className="acconto-dett__griglia">
              <div className="acconto-dett__campo">
                <span className="acconto-dett__campo-label">Data emissione</span>
                <span className="acconto-dett__campo-value">{formatDateIT(data.dataEmissione)}</span>
              </div>
              <div className="acconto-dett__campo">
                <span className="acconto-dett__campo-label">Cliente</span>
                <span className="acconto-dett__campo-value">
                  {data.clienteRagioneSociale || <span className="bop-text-muted">—</span>}
                </span>
              </div>
              <div className="acconto-dett__campo">
                <span className="acconto-dett__campo-label">Commessa</span>
                <span className="acconto-dett__campo-value">
                  {data.commessa ? data.commessa.numero : <span className="bop-text-muted">—</span>}
                </span>
              </div>
              <div className="acconto-dett__campo">
                <span className="acconto-dett__campo-label">Ordine</span>
                <span className="acconto-dett__campo-value">
                  {data.ordine ? (
                    <Link to={`/commerciale/attivo/ordini/${data.ordine.id}`}>{data.ordine.numero}</Link>
                  ) : (
                    <span className="bop-text-muted">—</span>
                  )}
                </span>
              </div>
              {acquirenteLabel && (
                <div className="acconto-dett__campo">
                  <span className="acconto-dett__campo-label">Acquirente</span>
                  <span className="acconto-dett__campo-value">{acquirenteLabel}</span>
                </div>
              )}
              <div className="acconto-dett__campo">
                <span className="acconto-dett__campo-label">Data pagamento</span>
                <span className="acconto-dett__campo-value">
                  {data.dataPagamento ? formatDateIT(data.dataPagamento) : <span className="bop-text-muted">—</span>}
                </span>
              </div>
            </div>

            {data.stato === 'ANNULLATA' && (
              <div className="acconto-dett__annullamento">
                <strong>Annullata il {formatDateIT(data.dataAnnullamento)}</strong>
                {data.motivoAnnullamento ? ` — ${data.motivoAnnullamento}` : ''}
              </div>
            )}
          </div>
        </section>

        {/* Importi */}
        <section className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Importi</h2>
          </div>
          <div className="bop-card__body">
            <div className="acconto-dett__importi">
              <div className="acconto-dett__importo-box">
                <span className="acconto-dett__importo-label">Imponibile</span>
                <span className="acconto-dett__importo-value">{formatCurrency(Number(data.imponibile))}</span>
              </div>
              <div className="acconto-dett__importo-box">
                <span className="acconto-dett__importo-label">IVA</span>
                <span className="acconto-dett__importo-value">{formatCurrency(Number(data.iva))}</span>
              </div>
              <div className="acconto-dett__importo-box acconto-dett__importo-box--highlight">
                <span className="acconto-dett__importo-label">Totale dovuto</span>
                <span className="acconto-dett__importo-value">{formatCurrency(Number(data.totale))}</span>
              </div>
              <div className="acconto-dett__importo-box">
                <span className="acconto-dett__importo-label">Incassato</span>
                <span className="acconto-dett__importo-value">{formatCurrency(Number(data.totaleIncassato))}</span>
              </div>
              <div className="acconto-dett__importo-box">
                <span className="acconto-dett__importo-label">Residuo</span>
                <span className="acconto-dett__importo-value">{formatCurrency(Number(data.residuo))}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Movimenti di incasso */}
        <section className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Movimenti di incasso</h2>
          </div>
          <div className="bop-card__body">
            {data.movimenti.length === 0 ? (
              <div className="bop-empty-state">Nessun movimento di incasso registrato.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data incasso</th>
                    <th className="bop-text-right">Importo</th>
                    <th>Canale</th>
                    <th>Contabile</th>
                    <th>Fattura</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {data.movimenti.map(m => (
                    <tr key={m.id}>
                      <td>{formatDateIT(m.dataIncasso)}</td>
                      <td className="bop-text-right">{formatCurrency(Number(m.importo))}</td>
                      <td>{m.canale || <span className="bop-text-muted">—</span>}</td>
                      <td>
                        {m.contabile ? (
                          m.contabile.nomeFile
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {m.fattura ? (
                          m.fattura.statoFattura === 'SDI' && m.fattura.numero ? (
                            <span>{m.fattura.numero}</span>
                          ) : (
                            <span className="bop-badge bop-badge-neutral">
                              Proforma (bozza #{m.fattura.id})
                            </span>
                          )
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>{m.note || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Log */}
        <section className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {data.logs.length === 0 ? (
              <div className="bop-empty-state">Nessuna attività registrata.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {modalIncasso && (
          <ModaleRegistraIncasso
            proformaId={proformaId}
            residuo={data.residuo}
            onClose={() => setModalIncasso(false)}
            onDone={() => {
              setModalIncasso(false);
              reload();
            }}
          />
        )}

        {modalAnnulla && (
          <ModaleAnnullaProforma
            proformaId={proformaId}
            numero={data.numero}
            onClose={() => setModalAnnulla(false)}
            onDone={() => {
              setModalAnnulla(false);
              reload();
            }}
          />
        )}

        {modalStampa && (
          <PrintModal
            societaId={data.societaId}
            entitaType="proforma_acconto"
            entitaId={data.id}
            entitaLabel={`Proforma ${data.numero}`}
            onClose={() => setModalStampa(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}

// ── Modale: Registra incasso ──────────────────────────────────────────────────

interface ModaleRegistraIncassoProps {
  proformaId: number;
  residuo: number;
  onClose: () => void;
  onDone: () => void;
}

function ModaleRegistraIncasso({ proformaId, residuo, onClose, onDone }: ModaleRegistraIncassoProps) {
  const oggi = new Date().toISOString().slice(0, 10);
  const [importo, setImporto] = useState<string>(residuo > 0 ? String(residuo) : '');
  const [dataIncasso, setDataIncasso] = useState<string>(oggi);
  const [note, setNote] = useState<string>('');
  const [scelta, setScelta] = useState<SceltaIncasso>('CHIUDI_A_IMPORTO_REALE');
  const [contabile, setContabile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const impNum = parseFloat(importo);
    if (isNaN(impNum) || impNum <= 0) {
      showToast('Importo non valido', 'error');
      return;
    }
    if (!dataIncasso) {
      showToast('Data incasso obbligatoria', 'error');
      return;
    }
    if (!contabile) {
      showToast('Contabile (file) obbligatorio', 'error');
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('importo', String(impNum));
      fd.append('dataIncasso', dataIncasso);
      if (note.trim()) fd.append('note', note.trim());
      fd.append('scelta', scelta);
      fd.append('contabile', contabile);

      // @api POST /api/proforme-acconto/:id/incassi {"importo":"number","dataIncasso":"string","note":"string?","scelta":"string","contabile":"File"} -> {"success":true,"data":{"movimentoId":"number","fatturaId":"number","fatturaStato":"string","statoProforma":"string","totaleIncassato":"number","residuo":"number","rettificaOrdine":"object|null"}}
      const res = await apiFetch<{ success: boolean; data: {
        movimentoId: number; fatturaId: number; fatturaStato: 'PROFORMA' | 'SDI';
        rettificaOrdine: { ordineId: number; accontoTotaleReale: number } | null;
      } }>(
        `/api/proforme-acconto/${proformaId}/incassi`,
        {
          method: 'POST',
          body: fd,
        }
      );
      showToast('Incasso registrato — fattura proforma generata', 'success');
      if (res.data?.rettificaOrdine) {
        showToast(
          `Acconto dell'ordine rettificato a importo reale: ${formatCurrency(res.data.rettificaOrdine.accontoTotaleReale)}`,
          'info'
        );
      }
      onDone();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nella registrazione', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Registra incasso"
      onClose={onClose}
      footer={
        <>
          <button className="bop-btn-secondary" onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button className="bop-btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Registra'}
          </button>
        </>
      }
    >
      <div className="acconto-dett__modal-form">
        <div className="bop-form-row">
          <div className="bop-form-group">
            <label className="bop-label">Importo *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="bop-input"
              value={importo}
              onChange={e => setImporto(e.target.value)}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Data incasso *</label>
            <input
              type="date"
              className="bop-input"
              value={dataIncasso}
              onChange={e => setDataIncasso(e.target.value)}
            />
          </div>
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Note</label>
          <textarea
            className="bop-input acconto-dett__modal-textarea"
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
          />
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Contabile (PDF/file) *</label>
          <input
            type="file"
            className="bop-input"
            onChange={e => setContabile(e.target.files?.[0] || null)}
          />
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Scelta gestione</label>
          <div className="acconto-dett__radio-group">
            <label className="acconto-dett__radio-item">
              <input
                type="radio"
                name="scelta"
                value="CHIUDI_A_IMPORTO_REALE"
                checked={scelta === 'CHIUDI_A_IMPORTO_REALE'}
                onChange={() => setScelta('CHIUDI_A_IMPORTO_REALE')}
              />
              <span className="acconto-dett__radio-text">
                <span className="acconto-dett__radio-title">Chiudi a importo reale</span>
                <span className="acconto-dett__radio-desc">
                  La proforma viene chiusa al totale effettivamente incassato.
                </span>
              </span>
            </label>
            <label className="acconto-dett__radio-item">
              <input
                type="radio"
                name="scelta"
                value="LASCIA_APERTA"
                checked={scelta === 'LASCIA_APERTA'}
                onChange={() => setScelta('LASCIA_APERTA')}
              />
              <span className="acconto-dett__radio-text">
                <span className="acconto-dett__radio-title">Lascia aperta</span>
                <span className="acconto-dett__radio-desc">
                  Registra il movimento, lascia la proforma in attesa del saldo.
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Modale: Annulla proforma ──────────────────────────────────────────────────

interface ModaleAnnullaProps {
  proformaId: number;
  numero: string;
  onClose: () => void;
  onDone: () => void;
}

function ModaleAnnullaProforma({ proformaId, numero, onClose, onDone }: ModaleAnnullaProps) {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!motivo.trim()) {
      showToast('Inserisci il motivo dell\'annullamento', 'error');
      return;
    }
    setSaving(true);
    try {
      // @api POST /api/proforme-acconto/:id/annulla {"motivoAnnullamento":"string"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/proforme-acconto/${proformaId}/annulla`, {
        method: 'POST',
        body: JSON.stringify({ motivoAnnullamento: motivo.trim() }),
      });
      showToast('Proforma annullata', 'success');
      onDone();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nell\'annullamento', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Annulla proforma ${numero}`}
      onClose={onClose}
      footer={
        <>
          <button className="bop-btn-secondary" onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button className="bop-btn-danger" onClick={submit} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Conferma annullamento'}
          </button>
        </>
      }
    >
      <div className="acconto-dett__modal-form">
        <div className="bop-form-group">
          <label className="bop-label">Motivo annullamento *</label>
          <textarea
            className="bop-input acconto-dett__modal-textarea"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={4}
            placeholder="Indica il motivo dell'annullamento della proforma"
          />
        </div>
      </div>
    </Modal>
  );
}
